import { FC } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useMutation } from 'react-query';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { useStore } from 'hooks/useRootStore';
import { ReactComponent as LogoIcon } from 'assets/logo.svg';
import { ReactComponent as LogoutIcon } from 'assets/navbar/navbar_logout.svg';
import { ReactComponent as ProfileIcon } from 'assets/navbar/navbar_profile.svg';
import { ICountryCodeData } from 'types/common';
import { navBarTranslations } from './translations';
import { NavBarTransFields } from './types';
import styles from './navbar.module.scss';

export const Navbar: FC = observer(() => {
  const {
    mainStore: { userNavItems, logout, user, changeCountryCode },
    tableStore: { setStartPageIndex },
    alertStore: { showAlert },
  } = useStore();

  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useMyTranslations<NavBarTransFields>([navBarTranslations.name]);

  const { isLoading, mutate } = useMutation(changeCountryCode, {
    mutationKey: 'changeCountryCode',
    onSuccess: () => {
      showAlert('Country has been changed', 'success');
    },
  });

  const onCountryCodeSelect = (ccItem: ICountryCodeData) => {
    if (user?.selectedCountryCode.countryCode !== ccItem.countryCode) {
      setStartPageIndex(0);
      mutate(ccItem);
    }
  };

  return (
    <div className={styles.navBar}>
      <div className={styles.navBarBox}>
        <Link to='/' className={styles.logo}>
          <LogoIcon />
        </Link>
        <div className={`${styles.countryCode}`}>
          <p className={styles.countryCodeTitle}>{user?.selectedCountryCode.countryCode}</p>
          <div className={styles.countryCodeBtnBox}>
            {user?.usersCountryCodes.map((item) => (
              <button
                key={item.countryCode}
                className={styles.countryCodeBtn}
                type='button'
                disabled={isLoading || !!id}
                onClick={() => onCountryCodeSelect(item)}>
                <span className={styles.countryCodeText}>{item.name}</span>
              </button>
            ))}
          </div>
        </div>
        <nav className={styles.navMenu}>
          {userNavItems.map((item, key) => (
            <div
              key={key}
              className={`${styles.navItem} ${
                window.location.pathname.startsWith(item.link.path) && styles.navItemsActive
              }`}>
              <button
                type='button'
                className={styles.navButton}
                onClick={() => {
                  setStartPageIndex(0);
                  navigate(item.link.path);
                }}
                disabled={item.disable}>
                <span className={styles.linkIcon}>{item.icon}</span>
                <span className={styles.iconText}>{t(item.label)}</span>
              </button>
            </div>
          ))}
        </nav>
        <button
          className={styles.navLink}
          type='button'
          onClick={() => {
            navigate('/profile');
          }}>
          <span className={`${styles.linkIcon} ${styles.profile}`}>
            <ProfileIcon />
          </span>
          <span className={styles.iconText}>{t('profile')}</span>
        </button>
        <button className={styles.navLink} type='button' onClick={logout}>
          <span className={`${styles.linkIcon} ${styles.logOut}`}>
            <LogoutIcon />
          </span>
          <span className={styles.iconText}>{t('logout')}</span>
        </button>
        <span className={styles.version}>{process.env.REACT_APP_APP_VERSION}</span>
      </div>
    </div>
  );
});
